/**
 * Un negocio de mentira, con datos de verdad.
 *
 * Setting a shop up by hand is the right flow for the person who owns one and the wrong flow
 * entirely for somebody trying to find out whether the software works. This fills a fresh
 * install with a day in the life of a pulpería in about two seconds: stock that was bought,
 * prices with real margins behind them, an open till, sales in several payment methods, a
 * transfer waiting to be confirmed, an order out for delivery, staff on the books.
 *
 * Every one of those goes through `emitir`, so it is validated, appended to the log, reduced
 * and projected exactly like the real thing. That matters: a fixture written straight into
 * Dexie would make every screen look right while proving nothing, and would be the first thing
 * to drift when a reducer changed. This cannot drift — if a reducer rejects it, the demo fails
 * loudly, which is itself a test.
 *
 * It is development-only, and checks that with the server rather than trusting the screen that
 * called it — see `permitido` below.
 */

import {
  CATALOGO_SEMILLA,
  cashRoundingDelta,
  centavos,
  fromUnits,
  hoy,
  lineTotal,
  newId,
  precioParaMargen,
  sumarDias,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { api } from '../sync/client.js';
import { abrirCaja, fondoQueQuedo, sesionAbierta, siguienteTicket } from './caja.js';
import {
  asegurarListasBase,
  crearProducto,
  listaPredeterminada,
  ordenarListas,
  precioDe,
} from './catalogo.js';
import { emitir } from './emitir.js';
import { fotoDeArchivo } from './fotos.js';
import { normalizar } from './texto.js';
import { enPractica } from './practica.js';
import { esCopiaDeTienda } from './copia-de-tienda.js';

const haceDias = (n: number) => sumarDias(hoy(), -n);
/** Today's weekday at the shop, 0 = domingo, the way `diaVisita` counts. */
const diaDeHoy = () => new Date(`${hoy()}T00:00:00Z`).getUTCDay();

/** Two products from each category, so every screen has variety without 140 rows of it. */
function surtido(): typeof CATALOGO_SEMILLA {
  const porCategoria = new Map<string, (typeof CATALOGO_SEMILLA)[number][]>();
  for (const p of CATALOGO_SEMILLA) {
    const xs = porCategoria.get(p.categoria);
    if (xs) xs.push(p);
    else porCategoria.set(p.categoria, [p]);
  }
  return [...porCategoria.values()].flatMap((xs) => xs.slice(0, 2));
}

/**
 * A plausible cost per base unit, from the product's position rather than from `Math.random`.
 *
 * Deterministic on purpose: two runs produce the same shop, so a number that looks wrong can be
 * chased instead of shrugged at.
 */
const costoDe = (i: number): Centavos =>
  centavos(Math.round((900 + ((i * 977) % 9100)) / 50) * 50);

/**
 * The demo's photos: stock photographs (`demo-fotos.ts`), put through exactly what a camera
 * photo goes through, so the demo exercises reduction, naming, storage and upload instead of
 * skipping them.
 *
 * Fetched on demand: twenty-four photos are no business of a real shop's first page load. A
 * device that cannot fetch one, such as a practice shop built with no signal, still gets its
 * shop. A photo is the one thing in a fixture that is fine to lose.
 */
async function fotoDePrueba(nombre: string): Promise<string | undefined> {
  try {
    const { urlFotoDemo } = await import('./demo-fotos.js');
    const url = urlFotoDemo(nombre);
    if (!url) return undefined;
    const respuesta = await fetch(url);
    if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
    return await fotoDeArchivo(await respuesta.blob());
  } catch (e) {
    console.warn('[demo] sin foto para', nombre, e);
    return undefined;
  }
}

export type Avisar = (paso: string) => void;
export type Resultado = 'creado' | 'yaHabia';

/**
 * A reducer refusing an event does not throw — it stores the event marked `rechazado` and moves
 * on, which is right for a shop mid-sale and wrong for a fixture. Left alone, the button would
 * say "Listo" over a half-built demo, and the missing half would be blamed on the screen that
 * failed to show it rather than on the event that never landed.
 *
 * So every id is kept and read back at the end. If anything was refused, this throws with the
 * reason, and the demo loader doubles as a test of the reducers it exercises.
 */
async function comprobar(ids: readonly string[]): Promise<void> {
  const eventos = await db.eventos.where('id').anyOf([...ids]).toArray();
  const malos = eventos.filter((e) => e.estado === 'rechazado');
  if (malos.length === 0) return;
  throw new Error(
    malos.map((e) => `${e.type}: ${e.motivoRechazo ?? 'rechazado'}`).join(' · '),
  );
}

/**
 * The second lock, and the one that matters.
 *
 * Hiding a button is a statement about the screen; this is a statement about the server. In a
 * production build `import.meta.env.DEV` is `false`, so the only way through is the API saying
 * it is not production and has no SETUP_TOKEN — which no client can assert on its own. Dev
 * keeps working offline because the first half short-circuits.
 */
async function permitido(): Promise<boolean> {
  // A practice shop is a throwaway database that never reaches the server, so seeding it is
  // safe everywhere — including production, which is exactly what makes the tutorial usable
  // by a cashier on a real phone in a real shop.
  if (enPractica()) return true;
  // A copy of the real shop runs on a development server too, and is the one it must not seed.
  if (esCopiaDeTienda()) return false;
  if (import.meta.env.DEV) return true;
  try {
    return Boolean((await api.health()).instalacionAbierta);
  } catch {
    return false;
  }
}

export async function cargarDemo(avisar: Avisar = () => {}): Promise<Resultado> {
  if (!(await permitido())) throw new Error('Solo en desarrollo');
  const productos = surtido();
  const emitidos: string[] = [];
  const registrar = async <T>(p: Promise<T>): Promise<T> => {
    const id = await p;
    if (typeof id === 'string') emitidos.push(id);
    return id;
  };

  // --- El negocio -----------------------------------------------------------
  avisar('Negocio');
  // No truck: «Reparto» went (the owner, 2026-10-04), and whoever delivers sells from a till.
  await registrar(emitir('config.cambiada', {
    clave: 'negocioNombre',
    valor: 'Pulpería La Esperanza',
    anterior: 'Abastece Ya',
  }));

  await asegurarListasBase();
  const listas = ordenarListas(await db.listas.toArray());
  const publico = listaPredeterminada(listas) ?? listas[0]!;
  const mayorista = listas.find((l) => l.id !== publico.id) ?? publico;

  // --- Catálogo con precios -------------------------------------------------
  // Priced off the cost it is about to be bought at, so every margin on screen is real
  // arithmetic rather than two numbers that happen to sit next to each other.
  avisar('Productos');
  const yaExiste = new Set((await db.productos.toArray()).map((p) => normalizar(p.nombre)));
  const creados: { productoId: string; unidadVentaId: string; costo: Centavos }[] = [];

  for (const [i, p] of productos.entries()) {
    if (yaExiste.has(normalizar(p.nombre))) continue;
    const costo = costoDe(i);
    const foto = await fotoDePrueba(p.nombre);
    const { productoId, unidadVentaId } = await crearProducto({
      nombre: p.nombre,
      nombreEn: p.nombreEn,
      ...(foto ? { foto } : {}),
      categoria: p.categoria,
      unidadBase: p.unidadBase,
      ...(p.esMedicamento ? { esMedicamento: true } : {}),
      ...(p.perecedero ? { perecedero: true } : {}),
      precios: [
        { listaId: publico.id, precio: precioParaMargen(costo, 3_500) },
        { listaId: mayorista.id, precio: precioParaMargen(costo, 2_000) },
      ],
    });
    creados.push({ productoId, unidadVentaId, costo });
  }
  // Already loaded. Running it twice is a no-op rather than a second identical shop.
  if (creados.length === 0) return 'yaHabia';

  // --- Proveedor y compra ---------------------------------------------------
  avisar('Compra');
  const proveedorId = newId();
  await registrar(emitir('proveedor.creado', {
    proveedorId,
    nombre: 'Distribuidora del Norte',
    telefono: '2265 4400',
    contacto: 'Don Ramón',
  }));

  const compraLineas = creados.map(({ productoId, costo }) => {
    const cantidadBase = fromUnits(40);
    return {
      productoId,
      cantidadBase,
      costoTotal: centavos(Math.round(costo * (cantidadBase / 1_000))),
    };
  });
  await registrar(emitir('compra.registrada', {
    compraId: newId(),
    proveedorId,
    fecha: haceDias(6),
    documento: 'F-88214',
    moneda: 'NIO',
    flete: centavos(45_000),
    // Into the shop, the one place there is (the owner, 2026-10-05: «we have just one location»).
    ubicacion: 'tienda',
    formaPago: 'credito',
    vencePago: haceDias(-24),
    lineas: compraLineas,
  }));

  // --- Clientes -------------------------------------------------------------
  avisar('Clientes');
  const clienteMayorista = newId();
  await registrar(emitir('cliente.creado', {
    clienteId: clienteMayorista,
    nombre: 'Pulpería Doña Chepita',
    tipo: 'mayorista',
    telefono: '8844 1122',
    direccion: 'De la iglesia 2 cuadras al sur',
    listaId: mayorista.id,
    diaVisita: diaDeHoy(),
    notas: 'Paga de contado los viernes',
  }));
  await registrar(emitir('cliente.creado', {
    clienteId: newId(),
    nombre: 'Comedor El Buen Sabor',
    tipo: 'mayorista',
    telefono: '8712 9080',
    direccion: 'Frente al parque central',
    listaId: mayorista.id,
    diaVisita: (diaDeHoy() + 2) % 7,
  }));
  // The neighbour who buys on fiado, as somebody always does: a limit, and one sale on it below.
  const donaMarta = newId();
  await registrar(emitir('cliente.creado', {
    clienteId: donaMarta,
    nombre: 'Doña Marta',
    tipo: 'mostrador',
    telefono: '8555 3311',
    limiteCredito: centavos(150_000),
  }));

  // --- Personal -------------------------------------------------------------
  avisar('Personal');
  await registrar(emitir('empleado.contratado', {
    empleadoId: newId(),
    nombre: 'Karla Mendoza',
    cargo: 'Cajera',
    salarioMensual: centavos(950_000),
    fechaIngreso: '2023-03-15',
    cedula: '001-150398-1002B',
    inssNumero: '9912345',
  }));
  await registrar(emitir('empleado.contratado', {
    empleadoId: newId(),
    nombre: 'Elvin Juárez',
    cargo: 'Repartidor',
    salarioMensual: centavos(880_000),
    fechaIngreso: '2024-08-01',
  }));

  // --- Caja y ventas --------------------------------------------------------
  avisar('Ventas');
  const abierta = await sesionAbierta();
  const sesionId =
    abierta?.id ??
    (await abrirCaja(centavos(200_000), 'tienda', centavos(0), { relevo: await fondoQueQuedo('tienda') }));

  const existencias = await db.existencias.toArray();
  const costoEn = (productoId: string) =>
    existencias.find((e) => e.id === existenciaId(productoId, 'tienda'))?.costoPromedio ?? 0;
  const precios = await db.precios.toArray();
  const precioEn = (unidadVentaId: string, listaId: string) =>
    precioDe(precios, unidadVentaId, listaId) ?? centavos(0);

  /** Five tickets: exact cash, cash with change, a card, a transfer nobody has verified, and fiado. */
  const guiones: { indices: number[]; cantidades: number[]; forma: string; cliente: string | null }[] = [
    { indices: [0, 3, 7], cantidades: [2, 1, 3], forma: 'efectivo_cordobas', cliente: null },
    { indices: [1, 5], cantidades: [1, 2], forma: 'vuelto', cliente: null },
    { indices: [2, 9, 11], cantidades: [4, 1, 1], forma: 'tarjeta', cliente: null },
    { indices: [4, 6], cantidades: [5, 2], forma: 'transferencia', cliente: clienteMayorista },
    { indices: [8, 10], cantidades: [2, 1], forma: 'credito', cliente: donaMarta },
  ];

  for (const g of guiones) {
    const usaMayorista = g.cliente === clienteMayorista;
    const listaId = usaMayorista ? mayorista.id : publico.id;
    const lineas = g.indices
      .map((idx, j) => {
        const c = creados[idx];
        if (!c) return null;
        const producto = productos[idx]!;
        const cantidad = fromUnits(g.cantidades[j]!) as Milli;
        const precio = centavos(precioEn(c.unidadVentaId, listaId));
        return {
          productoId: c.productoId,
          descripcion: producto.nombre,
          unidadVentaId: c.unidadVentaId,
          cantidad,
          factor: fromUnits(1),
          precioLista: precio,
          precioUnitario: precio,
          costoReferencia: costoEn(c.productoId),
          subtotal: lineTotal(precio, cantidad),
        };
      })
      .filter((l): l is NonNullable<typeof l> => l !== null);
    if (lineas.length === 0) continue;

    const exacto = centavos(lineas.reduce((a, l) => a + l.subtotal, 0));
    // Only cash gets rounded: centavo coins do not circulate, card and transfer are exact.
    const efectivo = g.forma === 'efectivo_cordobas' || g.forma === 'vuelto';
    const redondeo = efectivo ? cashRoundingDelta(exacto, 50) : 0;
    const total = centavos(exacto + redondeo);

    // Tendered minus change has to equal the total — the reducer enforces it, not the screen.
    const entregado = g.forma === 'vuelto' ? centavos(Math.ceil(total / 10_000) * 10_000) : total;
    const forma = g.forma === 'vuelto' ? 'efectivo_cordobas' : g.forma;

    await registrar(emitir('venta.registrada', {
      ventaId: newId(),
      ticket: await siguienteTicket(),
      sesionId,
      fecha: hoy(),
      ubicacion: 'tienda',
      listaId,
      clienteId: g.cliente,
      pedidoId: null,
      lineas,
      descuento: 0,
      redondeo,
      total,
      pagos: [
        {
          forma: forma as 'efectivo_cordobas',
          monto: entregado,
          ...(forma === 'transferencia'
            ? { referencia: 'BAC-778812', estado: 'pendiente' as const }
            : { estado: 'confirmado' as const }),
        },
      ],
      cambio: centavos(entregado - total),
    }));
  }

  // --- Un pedido para hoy ---------------------------------------------------
  avisar('Pedido');
  const delPedido = creados.slice(0, 3).map(({ productoId, unidadVentaId }, j) => {
    const cantidad = fromUnits(6 + j * 2) as Milli;
    const precio = centavos(precioEn(unidadVentaId, mayorista.id));
    return {
      productoId,
      unidadVentaId,
      cantidad,
      precioUnitario: precio,
      subtotal: lineTotal(precio, cantidad),
    };
  });
  await registrar(emitir('pedido.tomado', {
    pedidoId: newId(),
    clienteId: clienteMayorista,
    fecha: haceDias(1),
    fechaEntrega: hoy(),
    listaId: mayorista.id,
    lineas: delPedido,
    total: centavos(delPedido.reduce((a, l) => a + l.subtotal, 0)),
    nota: 'Entregar antes del mediodía',
  }));

  // --- Gastos y dinero del dueño -------------------------------------------
  avisar('Gastos');
  await registrar(emitir('gasto.registrado', {
    gastoId: newId(),
    fecha: haceDias(3),
    categoria: 'energia',
    descripcion: 'Recibo de luz',
    moneda: 'NIO',
    monto: centavos(187_500),
    formaPago: 'efectivo_cordobas',
    proveedorId: null,
    referencia: 'DISNORTE-40021',
  }));
  await registrar(emitir('gasto.registrado', {
    gastoId: newId(),
    fecha: haceDias(2),
    categoria: 'transporte',
    descripcion: 'Combustible del camión',
    moneda: 'NIO',
    monto: centavos(120_000),
    formaPago: 'efectivo_cordobas',
    proveedorId: null,
  }));
  await registrar(emitir('dueno.retiro', {
    movimientoId: newId(),
    fecha: haceDias(2),
    monto: centavos(300_000),
    origen: 'efectivo_cordobas',
    motivo: 'Gasto de la casa',
  }));

  await comprobar(emitidos);
  avisar('Listo');
  return 'creado';
}
